import { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { COLOR_BLACK, COLOR_LIGHT_GRAY, COLOR_GRAY_8, COLOR_ORANGE_1 } from 'shared/helpers/colors';
import { ClickAwayListener } from '@mui/material';
import { useSales, useWebsiteEditor } from 'shared/hooks/index';
import useAlert from 'shared/hooks/useAlert';
import { useTranslation } from 'react-i18next';

const Inner = styled.div<{ isBotPosition?: boolean }>`
  position: absolute;
  zoom: 1.1;
  top: -490px;
  left: 0;
  z-index: 400;

  ${({ isBotPosition }) =>
    isBotPosition
      ? `
      top: 85px;
      left: auto;
      right: -70px;
  `
      : `
    top: -484px;
    left: auto;
    right: -70px;
  `}
`;

const fadeInTwo = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOutTwo = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PopUpLink = styled.div<{ isBotPosition?: boolean }>`
  width: 585px;
  z-index: 55;
  background: #fff8e3;
  border-radius: 7px;
  border: 1px solid #acacac;
  cursor: default;
  color: ${COLOR_BLACK};
  animation: ${(props) => (props.isBotPosition ? fadeOutTwo : fadeInTwo)} 0.3s ease-in-out;

  @media screen and (max-width: 991px) {
    width: 475px;
    left: -10px;
  }

  @media screen and (max-width: 768px) {
    width: 375px;
  }

  @media screen and (max-width: 465px) {
    width: 300px;
  }

  @media screen and (max-width: 340px) {
    width: 270px;
    left: -99px;
  }
`;

const Heading = styled.h1`
  font-family: Plus Jakarta Sans;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 30px;
`;

const SelectItem = styled.div<{ isActive: boolean }>`
  display: flex;
  gap: 18px;
  font-size: 24px;
  align-items: center;
  cursor: pointer;
  font-weight: ${(props) => (props.isActive ? '700' : '400')};
`;

const IconSelect = styled.div<{ isActive: boolean }>`
  width: 30px;
  height: 30px;
  background: ${(props) => (props.isActive ? COLOR_ORANGE_1 : '#d4d4d4')};
  border-radius: 50%;
  border: ${(props) => (props.isActive ? 'none' : '3px solid #c8c8c8')};
`;

const LineItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLOR_GRAY_8};
  margin: 3px 0;

  p {
    font-size: 20px;
    font-weight: 400;
    padding: 10px;
    position: relative;
    flex-shrink: 0;
  }
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  background: ${COLOR_GRAY_8};
  opacity: 0.5;
`;

const ClosePopUpLink = styled.img`
  position: absolute;
  right: 15px;
  top: 15px;
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const InnerRelativeLink = styled.div`
  position: relative;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 4px 7px 0px;
  border-radius: 7px;
`;

const PopUpInner = styled.div`
  padding: 28px 34px 48px;
  text-align: left;

  @media screen and (max-width: 991px) {
    padding: 20px 24px 28px;
  }
`;

const Form = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 26px;
`;

const LinkInput = styled.input`
  width: 100%;
  height: 70px;
  border-radius: 10px;
  border: 1px solid ${COLOR_LIGHT_GRAY};
  padding: 10px 16px;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.12));
  transition: filter 0.3s ease;
  outline: none;
  font-family: Plus Jakarta Sans;
  font-size: 23px;
  background: #fff;

  &:hover {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0));
    transition: filter 0.3s ease;
  }

  &:disabled {
    background: rgb(255, 255, 255);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0));
    opacity: 0.9;
    cursor: not-allowed;
    border-color: #ddd;
  }
`;

const ApplyBtn = styled.button`
  height: 76px;
  width: 100%;
  font-family: Plus Jakarta Sans;
  display: flex;
  align-items: center;
  font-weight: 700;
  justify-content: center;
  border-radius: 10px;
  border: none;
  background: ${COLOR_ORANGE_1};
  font-size: 22px;
  gap: 16px;
  transform: translateY(0px);
  box-shadow: rgba(0, 0, 0, 0) 0px 0rem 0rem;
  transition:
    transform 0.2s ease 0s,
    box-shadow 0.2s ease 0s;

  &:hover {
    cursor: pointer;
    transform: translateY(-3px);
    box-shadow: rgba(0, 0, 0, 0.12) 0px 1rem 2rem;
    transition:
      transform 0.2s ease 0s,
      box-shadow 0.2s ease 0s;
  }

  img {
    width: 34px;
    height: 34px;
  }
`;

const SvgArrowBot = styled.svg<{ isBotPosition?: boolean }>`
  position: absolute;
  width: 30px;
  height: 14px;
  fill: none;
  z-index: 33;

  ${({ isBotPosition }) =>
    isBotPosition
      ? `
      top: -12px;
      right: 27px;
      transform: rotate(0deg);
  `
      : `
    bottom: -12px;
    right: 27px;
    transform: rotate(180deg);
  `}
`;

interface ButtonActionModalProps {
  onClose: () => void;
  role?: string;
  meta: Record<string, unknown>;
  isBotPosition?: boolean;
  benefitIndex?: number;
}

const ButtonActionModal = ({
  onClose,
  meta,
  isBotPosition,
  role,
  benefitIndex
}: ButtonActionModalProps) => {
  const { updateButtonUrl } = useSales();
  const { addAlert } = useAlert();
  const { containerClassname } = useWebsiteEditor();
  const { t } = useTranslation();

  const modalItem = useRef<HTMLDivElement | null>(null);

  const [isNextStepChecked, setIsNextStepChecked] = useState(!meta?.['url']);

  console.log('OPENED', meta, benefitIndex);
  const [link, setLink] = useState(
    (meta?.['url'] as string) || (meta?.[`url_${benefitIndex}`] as string) || ''
  );
  const [isDisabled, setIsDisabled] = useState(false);
  const [selectedElement, setSelectedElement] = useState('');

  useEffect(() => {
    if (link) {
      setSelectedElement('urlCustom');
    } else {
      setSelectedElement('nextStep');
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  // Apply styles to the parent element of the modal, needed for positioning and avoid cutting off
  useEffect(() => {
    const parentElement = modalItem.current?.closest(
      `.${containerClassname}`
    ) as HTMLElement | null;
    const oneOfCard = modalItem.current?.closest('.card-item__btn_choosen') as HTMLElement | null;

    if (!parentElement) return;

    if (!isDisabled) {
      parentElement.style.position = 'relative';
      parentElement.style.overflow = 'visible';
      parentElement.style.overflowX = 'clip';
      parentElement.style.zIndex = '200';
    }

    if (oneOfCard && !isDisabled) {
      oneOfCard.style.position = 'relative';
      oneOfCard.style.zIndex = '100';
    }

    return () => {
      parentElement.style.removeProperty('position');
      parentElement.style.removeProperty('overflow');
      parentElement.style.removeProperty('overflow-x');
      parentElement.style.removeProperty('z-index');

      if (oneOfCard) {
        oneOfCard.style.removeProperty('position');
        oneOfCard.style.removeProperty('z-index');
      }
    };
  }, [isDisabled, containerClassname]);

  // const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?(\?\S*)?(#\S*)?$/;
  // const isValidUrl = (url: string) => urlRegex.test(url);

  function isValidUrl(url: string): boolean {
    try {
      const u = new URL(url);
      const hostname = u.hostname;

      const domainParts = hostname.split('.');
      const tld = domainParts.at(-1);
      const hasValidTLD = domainParts.length >= 2 && tld !== undefined && tld.length >= 2;

      return ['http:', 'https:'].includes(u.protocol) && hasValidTLD;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  const handleSubmit = () => {
    let finalLink = link;

    if (!finalLink.startsWith('http://') && !finalLink.startsWith('https://')) {
      finalLink = `https://${finalLink}`;
    }

    if (!isValidUrl(finalLink)) {
      addAlert(
        t('common.btnAction.error', { defaultValue: 'Please enter a valid URL' }),
        'warning'
      );
      return;
    }

    console.log('benefitIndex', benefitIndex);
    updateButtonUrl(meta['accessorKey'] as string, finalLink, false, benefitIndex);
    setIsNextStepChecked(false);
    setIsDisabled(false);
    onClose();
    addAlert(
      t('common.btnAction.successUrl', { defaultValue: 'Button URL updated successfully' }),
      'success'
    );
  };

  const handleNextStepCheckbox = () => {
    setIsNextStepChecked(!isNextStepChecked);
    updateButtonUrl(meta['accessorKey'] as string, link, true);
    setIsDisabled(!isNextStepChecked);
    if (meta['accessorKey']) {
      addAlert(
        t('common.btnAction.successNextStep', {
          defaultValue: 'Button will link to the next step'
        }),
        'success'
      );
      onClose();
    }
  };

  return (
    <Inner
      ref={modalItem}
      isBotPosition={isBotPosition}
      aria-label="Button configuration modal"
      role="dialog"
      aria-modal="true">
      <ClickAwayListener onClickAway={() => onClose()}>
        <PopUpLink isBotPosition={isBotPosition} role={role}>
          <InnerRelativeLink>
            <ClosePopUpLink onClick={onClose} src="/img/close_link.svg" alt="X" />
            <PopUpInner>
              <Heading>{t('common.btnAction.title', { defaultValue: 'Button Action:' })}</Heading>
              <SelectItem
                onClick={() => setSelectedElement('nextStep')}
                isActive={selectedElement === 'nextStep'}>
                <IconSelect isActive={selectedElement === 'nextStep'} />
                <p>{t('common.btnAction.nextStep', { defaultValue: 'Link to the next step' })}</p>
              </SelectItem>
              <LineItem>
                <Line />
                <p>{t('common.btnAction.or', { defaultValue: 'or' })}</p>
                <Line />
              </LineItem>
              <SelectItem
                onClick={() => setSelectedElement('urlCustom')}
                isActive={selectedElement === 'urlCustom'}>
                <IconSelect isActive={selectedElement === 'urlCustom'} />
                <p>
                  {t('common.btnAction.urlTitle', {
                    defaultValue: 'Change the URL destination:'
                  })}
                </p>
              </SelectItem>

              <Form>
                <LinkInput
                  disabled={selectedElement !== 'urlCustom'}
                  placeholder="https://app.pagewheel.com/"
                  value={link}
                  onChange={(e) => setLink(e.target.value.trim())}
                  onClick={(e) => {
                    e.preventDefault();
                    e.currentTarget.focus();
                  }}
                />
                <ApplyBtn
                  type="button"
                  onClick={selectedElement === 'nextStep' ? handleNextStepCheckbox : handleSubmit}>
                  <img src={'/img/color_check.svg'} alt="✓" />
                  <p>{t('common.btnAction.btn', { defaultValue: 'Apply' })}</p>
                </ApplyBtn>
              </Form>
            </PopUpInner>

            <SvgArrowBot
              isBotPosition={isBotPosition}
              className="svgBot"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 21 11">
              <path d="M11 1L20.5 10.5H0.5L11 1Z" fill="#FFF8E3" />
              <path d="M20 9.5L11 1L1 9.5" stroke="#ACACAC" strokeLinecap="round" />
            </SvgArrowBot>
          </InnerRelativeLink>
        </PopUpLink>
      </ClickAwayListener>
    </Inner>
  );
};

export default ButtonActionModal;
